// One road for every share card (score, Daily, Atlas, badge), and the count
// of how each one ended.
//
// Before this, each screen carried its own copy of the road, and a player who
// closed the Android share sheet was handed a second one: a cancel threw, and
// the catch fell through to the text share. Nothing recorded a tap either, so
// whether anyone shares at all was unknown. Now a tap writes share_tap, the
// end writes share_result with how it ended, and a cancel is a cancel.
// What stays invisible: whether the chosen app actually sent anything.
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { blobToBase64 } from './shareCard';
import { logAppEvent } from './appEvents';

export type ShareCardKind = 'score' | 'daily' | 'atlas' | 'badge';
export type ShareOutcome = 'shared' | 'cancelled' | 'copied' | 'preview' | 'failed';
type Props = Record<string, string | number | boolean | null>;

export interface ShareRequest {
  card: ShareCardKind;
  /** The rendered card; null shares text alone. */
  blob: Blob | null;
  fileName: string;
  /** The message, without the link. */
  text: string;
  url: string;
  dialogTitle: string;
  /** On the web: 'sheet' offers the image to a share sheet that takes files (phones),
   *  'preview' shows the card with copy and save (a desktop sheet drops the text). */
  web: 'sheet' | 'preview';
  props?: Props;
}

export interface ShareDone {
  outcome: ShareOutcome;
  /** The card as an object URL, for the screen to show when it could not be sent. */
  previewUrl?: string;
}

/** Android's sheet rejects with "Share canceled"; the Web Share API with AbortError. */
export function isShareCancel(err: unknown): boolean {
  const e = err as { name?: string; message?: string } | null;
  return e?.name === 'AbortError' || /cancel/i.test(String(e?.message ?? ''));
}

/** The app Android says the player picked, as its package name. */
function pickedApp(activityType: string | undefined): string | null {
  if (!activityType) return null;
  return activityType.split('/')[0].slice(0, 60);
}

export async function shareCard(req: ShareRequest): Promise<ShareDone> {
  logAppEvent('share_tap', { card: req.card, ...req.props });
  const done = await run(req);
  logAppEvent('share_result', { card: req.card, outcome: done.outcome, app: done.app ?? null, ...req.props });
  return { outcome: done.outcome, previewUrl: done.previewUrl };
}

async function run(req: ShareRequest): Promise<ShareDone & { app?: string | null }> {
  const withLink = `${req.text}\n${req.url}`;

  // 1) The image, where the platform takes a file.
  if (req.blob) {
    try {
      if (Capacitor.isNativePlatform()) {
        const saved = await Filesystem.writeFile({
          path: req.fileName, data: await blobToBase64(req.blob), directory: Directory.Cache,
        });
        const r = await Share.share({ title: 'World of Trivia', text: withLink, files: [saved.uri], dialogTitle: req.dialogTitle });
        return { outcome: 'shared', app: pickedApp(r?.activityType) };
      }
      if (req.web === 'preview') return { outcome: 'preview', previewUrl: URL.createObjectURL(req.blob) };
      const file = new File([req.blob], req.fileName, { type: 'image/png' });
      const nav = navigator as any;
      if (nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: 'World of Trivia', text: withLink });
        return { outcome: 'shared' };
      }
    } catch (e) {
      if (isShareCancel(e)) return { outcome: 'cancelled' };
      // The image could not go; the text still can.
    }
  }

  // 2) Text, with the link in its own field.
  try {
    const r = await Share.share({ title: 'World of Trivia', text: req.text, url: req.url, dialogTitle: req.dialogTitle });
    return { outcome: 'shared', app: pickedApp(r?.activityType) };
  } catch (e) {
    if (isShareCancel(e)) return { outcome: 'cancelled' };
  }

  // 3) A desktop browser: copy it, and show the card so the share is not invisible.
  let copied = false;
  try { await navigator.clipboard.writeText(withLink); copied = true; } catch { /* blocked */ }
  const previewUrl = req.blob ? URL.createObjectURL(req.blob) : undefined;
  return { outcome: copied ? 'copied' : previewUrl ? 'preview' : 'failed', previewUrl };
}
